import { messageFromCreateUserError } from "@/components/user/lib";
import { SystemRole } from "@/data/use-users-data";
import { GroupRole } from "@/lib/consts";
import { queryKeys } from "@/lib/query-keys";
import { useQueryClient } from "@tanstack/react-query";
import {
  groupUserLinkControllerLinkUser,
  groupUserControllerCreateUser,
  userControllerCreateUser,
} from "../lib/gen-api";

export function useCreateUserAction() {
  const queryClient = useQueryClient();

  return {
    createUser: async (
      email: string,
      systemRole: SystemRole,
      groupUuids: string[],
      groupRole?: GroupRole,
    ) => {
      // If exactly one group and a group role is provided, use the
      // group-specific endpoint which adds an existing account to the team.
      if (groupUuids.length === 1 && groupRole) {
        const groupUuid = groupUuids[0];
        const response = await groupUserControllerCreateUser({
          path: { groupUuid },
          body: { email: email.trim(), role: groupRole },
        });

        if (response.error || !response.data?.email) {
          if (response.response.status === 404) {
            throw new Error("No account exists for this email");
          }
          throw new Error(
            response.error
              ? messageFromCreateUserError(response.error)
              : "Failed to add user",
          );
        }

        queryClient.invalidateQueries({
          queryKey: queryKeys.groupUsers(groupUuid),
        });

        return response.data;
      }

      // Otherwise, create user at system level and link to groups.
      const createResponse = await userControllerCreateUser({
        body: {
          email: email.trim(),
          role: systemRole,
        },
      });

      const user = createResponse.data;

      if (!user) {
        throw new Error("Failed to create user");
      }

      const promises = groupUuids.map((groupUuid) =>
        groupUserLinkControllerLinkUser({
          path: {
            userUuid: user.uuid,
            groupUuid,
          },
        }),
      );

      await Promise.all(promises);

      queryClient.invalidateQueries({
        queryKey: queryKeys.users(),
      });

      return user;
    },
  };
}
